<script>
import Chart from 'chart.js/auto';

export default {
  props: {
    datumOd: String,
    datumDo: String,
    primerjavaLeto: Number
  },
  emits: ['odjava'],
  data() {
    return {
      nalaganje: false,
      podatki: null,
      primerjava: null,
      trendChart: null,
      razdelitevChart: null,
      steviloChart: null,
      chartInitTimeout: null,
      activeRequestToken: 0,
      isMounted: true,
    }
  },
  mounted() {
    this.pridobiPodatke();
  },
  beforeUnmount() {
    this.isMounted = false;
    this.activeRequestToken += 1;
    if (this.chartInitTimeout) {
      clearTimeout(this.chartInitTimeout);
      this.chartInitTimeout = null;
    }
    this.destroyCharts();
  },
  watch: {
    datumOd() {
      this.pridobiPodatke();
    },
    datumDo() {
      this.pridobiPodatke();
    },
    primerjavaLeto() {
      this.pridobiPodatke();
    },
  },
  computed: {
    razlika() {
      if (!this.podatki || !this.primerjava) return null;

      const urePodatki = this.parseTime(this.podatki.skupno_cas);
      const urePrimerjava = this.parseTime(this.primerjava.skupno_cas);

      const razlikaMin = urePodatki - urePrimerjava;
      const pozitivna = razlikaMin >= 0;
      const absolutnaRazlika = Math.abs(razlikaMin);

      const ure = Math.floor(absolutnaRazlika / 60);
      const minute = absolutnaRazlika % 60;

      return {
        pozitivna,
        cas: `${ure}:${minute.toString().padStart(2, '0')}`,
        odstotek: urePrimerjava > 0 ? ((razlikaMin / urePrimerjava) * 100).toFixed(1) : 0
      };
    }
  },
  methods: {
    premakniDatumVPrimerjalnoLeto(datum, leto) {
      if (!datum || !leto) return null;
      const deli = datum.split('-');
      if (deli.length !== 3) return null;
      const mesec = parseInt(deli[1], 10);
      const dan = parseInt(deli[2], 10);
      if (!mesec || !dan) return null;
      const maxDan = new Date(leto, mesec, 0).getDate();
      const novDan = Math.min(dan, maxDan);
      return `${leto}-${deli[1]}-${String(novDan).padStart(2, '0')}`;
    },
    pridobiPodatke() {
      if (!this.datumOd || !this.datumDo) {
        this.podatki = null;
        this.primerjava = null;
        this.nalaganje = false;
        this.destroyCharts();
        return;
      }

      const requestToken = ++this.activeRequestToken;
      this.nalaganje = true;
      this.destroyCharts();

      // Clear any pending chart initialization
      if (this.chartInitTimeout) {
        clearTimeout(this.chartInitTimeout);
        this.chartInitTimeout = null;
      }

      const podatki = new URLSearchParams();
      podatki.append('datum_od', this.datumOd);
      podatki.append('datum_do', this.datumDo);

      axios.post('./api/porocila/vse/', podatki, {withCredentials: true})
          .then((response) => {
            if (!this.isMounted || requestToken !== this.activeRequestToken) return;

            this.podatki = response.data.podatki;
            this.nalaganje = false;

            // Debounce chart initialization to prevent rapid recreation
            this.chartInitTimeout = setTimeout(() => {
              this.$nextTick(() => {
                if (!this.isMounted || requestToken !== this.activeRequestToken || !this.podatki) return;
                if (this.podatki) {
                  this.initCharts();
                }
              });
            }, 100);

            // Če je vklopljena primerjava, pridobi tudi podatke za primerjavo
            if (this.primerjavaLeto) {
              this.pridobiPrimerjavo(requestToken);
            } else {
              this.primerjava = null;
            }
          })
          .catch((napaka) => {
            if (!this.isMounted || requestToken !== this.activeRequestToken) return;
            if (napaka.response?.status === 401) {
              this.$router.push('/Logout');
            }
            this.podatki = null;
            this.destroyCharts();
            this.nalaganje = false;
          });
    },
    pridobiPrimerjavo(requestToken = this.activeRequestToken) {
      const primerjavaDatumOd = this.premakniDatumVPrimerjalnoLeto(this.datumOd, this.primerjavaLeto);
      const primerjavaDatumDo = this.premakniDatumVPrimerjalnoLeto(this.datumDo, this.primerjavaLeto);
      if (!primerjavaDatumOd || !primerjavaDatumDo) {
        this.primerjava = null;
        return;
      }

      const podatki = new URLSearchParams();
      podatki.append('datum_od', primerjavaDatumOd);
      podatki.append('datum_do', primerjavaDatumDo);

      axios.post('./api/porocila/vse/', podatki, {withCredentials: true})
          .then((response) => {
            if (!this.isMounted || requestToken !== this.activeRequestToken) return;
            this.primerjava = response.data.podatki;
          })
          .catch(() => {
            if (!this.isMounted || requestToken !== this.activeRequestToken) return;
            this.primerjava = null;
          });
    },
    destroyCharts() {
      if (this.chartInitTimeout) {
        clearTimeout(this.chartInitTimeout);
        this.chartInitTimeout = null;
      }

      try {
        if (this.trendChart) {
          this.trendChart.stop();
          this.trendChart.destroy();
          this.trendChart = null;
        }
      } catch (e) {
        this.trendChart = null;
      }

      try {
        if (this.razdelitevChart) {
          this.razdelitevChart.stop();
          this.razdelitevChart.destroy();
          this.razdelitevChart = null;
        }
      } catch (e) {
        this.razdelitevChart = null;
      }

      try {
        if (this.steviloChart) {
          this.steviloChart.stop();
          this.steviloChart.destroy();
          this.steviloChart = null;
        }
      } catch (e) {
        this.steviloChart = null;
      }
    },
    initCharts() {
      if (!Chart || !this.podatki || !this.isMounted || this.nalaganje) return;
      this.destroyCharts();

      const isCanvasValid = (canvas) => {
        if (!canvas || !canvas.getContext || !canvas.ownerDocument || !canvas.isConnected || !document.body.contains(canvas)) {
          return false;
        }
        const context2d = canvas.getContext('2d');
        return !!(context2d && typeof context2d.save === 'function' && typeof context2d.restore === 'function');
      };

      const createChartSafe = (canvas, config) => {
        if (!isCanvasValid(canvas)) return null;
        const context2d = canvas.getContext('2d');
        if (!context2d || typeof context2d.save !== 'function') return null;
        try {
          if (Chart.getChart) {
            const existingChart = Chart.getChart(canvas);
            if (existingChart) {
              try {
                existingChart.stop();
                existingChart.destroy();
              } catch (e) {
                // ignore stale instance cleanup issues
              }
            }
          }
          const chartConfig = {
            ...config,
            options: {
              animation: false,
              ...(config.options || {})
            }
          };
          return new Chart(context2d, chartConfig);
        } catch (e) {
          return null;
        }
      };

      if (isCanvasValid(this.$refs.razdelitevChartCanvas)) {
        const motorni = this.parseTime(this.podatki.motorni_cas);
        const jadralni = this.parseTime(this.podatki.jadralni_cas);
        const vleke = this.parseTime(this.podatki.vleke_cas);

        this.razdelitevChart = createChartSafe(this.$refs.razdelitevChartCanvas, {
          type: 'doughnut',
          data: {
            labels: ['Motorni', 'Jadralni', 'Vleke'],
            datasets: [{
              data: [motorni, jadralni, vleke],
              backgroundColor: ['#60a5fa', '#34d399', '#fbbf24'],
              borderColor: 'rgba(255, 255, 255, 0.2)',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {color: '#ffffff'}
              }
            }
          }
        });
      }

      if (isCanvasValid(this.$refs.steviloChartCanvas)) {
        this.steviloChart = createChartSafe(this.$refs.steviloChartCanvas, {
          type: 'bar',
          data: {
            labels: ['Motorni', 'Jadralni', 'Vleke'],
            datasets: [{
              label: 'Število',
              data: [
                Number(this.podatki.motorni_stevilo) || 0,
                Number(this.podatki.jadralni_stevilo) || 0,
                Number(this.podatki.vleke_stevilo) || 0,
              ],
              backgroundColor: ['rgba(96, 165, 250, 0.8)', 'rgba(52, 211, 153, 0.8)', 'rgba(251, 191, 36, 0.8)'],
              borderColor: ['rgba(96, 165, 250, 1)', 'rgba(52, 211, 153, 1)', 'rgba(251, 191, 36, 1)'],
              borderWidth: 1,
              borderRadius: 6
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {display: false}
            },
            scales: {
              x: {
                ticks: {color: '#ffffff'},
                grid: {color: 'rgba(255, 255, 255, 0.12)'}
              },
              y: {
                beginAtZero: true,
                ticks: {color: '#ffffff', precision: 0},
                grid: {color: 'rgba(255, 255, 255, 0.12)'}
              }
            }
          }
        });
      }

      const trendData = Array.isArray(this.podatki.mesecni_trend) ? this.podatki.mesecni_trend : [];
      if (trendData.length > 0 && isCanvasValid(this.$refs.trendChartCanvas)) {
        const labels = trendData.map((item) => item.mesec_naziv || item.mesec);
        const values = trendData.map((item) => this.parseTime(item.cas));

        this.trendChart = createChartSafe(this.$refs.trendChartCanvas, {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: 'Skupne ure (minute)',
              data: values,
              borderColor: 'rgba(96, 165, 250, 1)',
              backgroundColor: 'rgba(96, 165, 250, 0.2)',
              fill: true,
              tension: 0.3,
              pointRadius: 3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {color: '#ffffff'}
              }
            },
            scales: {
              x: {
                ticks: {color: '#ffffff'},
                grid: {color: 'rgba(255, 255, 255, 0.12)'}
              },
              y: {
                beginAtZero: true,
                ticks: {color: '#ffffff'},
                grid: {color: 'rgba(255, 255, 255, 0.12)'}
              }
            }
          }
        });
      }
    },
    parseTime(timeString) {
      if (!timeString) return 0;
      const [ure, minute] = timeString.split(':').map(Number);
      return (ure * 60) + minute;
    },
    formatNumber(num) {
      return num ? num.toLocaleString('sl-SI') : '0';
    }
  }
}
</script>
<template>
  <div>
    <div v-if="nalaganje" class="text-center p-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Nalaganje...</span>
      </div>
    </div>

    <div v-else-if="podatki" class="row g-4">
      <!-- Skupne ure letenja -->
      <div class="col-md-6 col-lg-3">
        <div class="card bg-cards text-white">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-white">
              <i class="fas fa-clock"></i> Skupne ure letenja
            </h6>
            <h3 class="card-title mb-0">{{ podatki.skupno_cas }}</h3>
            <small v-if="primerjava" class="text-white">
								<span :class="razlika.pozitivna ? 'text-success' : 'text-danger'">
									<i :class="razlika.pozitivna ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
									{{ razlika.cas }} ({{ razlika.odstotek }}%)
								</span>
              <br>vs. {{ primerjavaLeto }}
            </small>
          </div>
        </div>
      </div>

      <!-- Motorni let -->
      <div class="col-md-6 col-lg-3">
        <div class="card bg-cards text-white">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-white">
              <i class="fas fa-plane-departure"></i> Motorni let
            </h6>
            <h3 class="card-title mb-0">{{ podatki.motorni_cas }}</h3>
            <small class="text-white">{{ formatNumber(podatki.motorni_stevilo) }} letov</small>
            <br>
            <small v-if="primerjava" class="text-white">vs. {{ primerjava.motorni_cas }} ({{ primerjavaLeto }})</small>
          </div>
        </div>
      </div>

      <!-- Jadralni let -->
      <div class="col-md-6 col-lg-3">
        <div class="card bg-cards text-white">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-white">
              <i class="fas fa-paper-plane"></i> Jadralni let
            </h6>
            <h3 class="card-title mb-0">{{ podatki.jadralni_cas }}</h3>
            <small class="text-white">{{ formatNumber(podatki.jadralni_stevilo) }} letov</small>
            <br>
            <small v-if="primerjava" class="text-white">vs. {{ primerjava.jadralni_cas }} ({{ primerjavaLeto }})</small>
          </div>
        </div>
      </div>

      <!-- Vleke -->
      <div class="col-md-6 col-lg-3">
        <div class="card bg-cards text-white">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-white">
              <i class="fas fa-link"></i> Vleke
            </h6>
            <h3 class="card-title mb-0">{{ podatki.vleke_cas }}</h3>
            <small class="text-white">{{ formatNumber(podatki.vleke_stevilo) }} vlek</small>
            <br>
            <small v-if="primerjava" class="text-white">vs. {{ primerjava.vleke_cas }} ({{ primerjavaLeto }})</small>
          </div>
        </div>
      </div>

      <!-- Razdelitev ur -->
      <div class="col-md-6 col-lg-6">
        <div class="card bg-cards text-white h-100">
          <div class="card-header">
            <h5 class="mb-0"><i class="fas fa-chart-pie"></i> Razdelitev ur</h5>
          </div>
          <div class="card-body" style="min-height: 260px;">
            <canvas ref="razdelitevChartCanvas"></canvas>
          </div>
        </div>
      </div>

      <!-- Razdelitev števila letov -->
      <div class="col-md-6 col-lg-6">
        <div class="card bg-cards text-white h-100">
          <div class="card-header">
            <h5 class="mb-0"><i class="fas fa-chart-pie"></i> Število aktivnosti</h5>
          </div>
          <div class="card-body" style="min-height: 260px;">
            <canvas ref="steviloChartCanvas"></canvas>
          </div>
        </div>
      </div>

      <!-- Dodatni podatki -->
      <div class="col-12">
        <div class="card bg-cards text-white">
          <div class="card-header">
            <h5 class="mb-0">Dodatni podatki</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-3">
                <div class="border-start border-primary border-3 ps-3">
                  <small class="text-white d-block">Število pilotov</small>
                  <strong class="fs-4">{{ formatNumber(podatki.st_pilotov) }}</strong>
                </div>
              </div>
              <div class="col-md-3">
                <div class="border-start border-info border-3 ps-3">
                  <small class="text-white d-block">Število letal</small>
                  <strong class="fs-4">{{ formatNumber(podatki.st_letal) }}</strong>
                </div>
              </div>
              <div class="col-md-3">
                <div class="border-start border-success border-3 ps-3">
                  <small class="text-white d-block">Pristankov</small>
                  <strong class="fs-4">{{ formatNumber(podatki.st_pristankov) }}</strong>
                </div>
              </div>
              <div class="col-md-3">
                <div class="border-start border-warning border-3 ps-3">
                  <small class="text-white d-block">Povprečno ur/let</small>
                  <strong class="fs-4">{{ podatki.povprecno_cas }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Graf mesečnega trenda (samo pri letnem prikazu) -->
      <div class="col-12" v-if="podatki.mesecni_trend && podatki.mesecni_trend.length">
        <div class="card bg-cards text-white">
          <div class="card-header">
            <h5 class="mb-0">Mesečni trend ({{ datumOd }} - {{ datumDo }})</h5>
          </div>
          <div class="card-body" style="min-height: 280px;">
            <canvas ref="trendChartCanvas"></canvas>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="alert alert-warning">
      <i class="fas fa-exclamation-triangle"></i> Ni podatkov za izbrano obdobje.
    </div>
  </div>
</template>